import React, { useEffect, useState } from 'react';
import { ConfirmButton, InputIdentifier } from '../../VehicleIdentifier.styles';
import { ErrorMessage, ManualForm, ManualFormLabel } from './AgriculturalMachine.styles';
import VehicleData from './models/vehicle-data.entity';
import VehicleTypeOption from './models/vehicle-type-option.entity';
import VehicleTypeValidation, { VehicleTypeValidationStatusEnum } from './models/vehicle-type-validation.entity';
import agriculturalMachineServices from './services/agricultural-machine.services';
import { AxiosResponse } from 'axios';
import AgricituralMachine from './models/agricultural-machine.entity';
import { LocalStoreKeysEnum } from '../../../../models/enums/localstore-keys.enum';
import authService from '../../../../auth/auth.service';
import AuthUser from '../../../../auth/models/auth-user.entity';
import { useHistory } from 'react-router-dom';
import PlateInfo from '../../../../models/plate-info.entity';
import storageService from '../../../../services/storage.service';
import { CircularProgress } from '@mui/material';
import whitelabelService from '../../../../services/whitelabel/whitelabel.service';

const HTTP_FORBIDDEN = 403;
const HTTP_TOO_MANY_REQUESTS = 429;
const TOO_MANY_REQUESTS_MESSAGE = 'Muitas tentativas em pouco tempo. Aguarde um minuto e tente novamente.';
const VEHICLE_TYPES_ERROR_MESSAGE = 'Não foi possível carregar os tipos de veículo. Tente novamente em instantes.';
const YEAR_FORMAT_ERROR_MESSAGE = 'O ano deve ter 4 números.';
const YEAR_LENGTH = 4;
const YEAR_FORMAT = /^\d{4}$/;

const identifierStyle = {
  disabledBtn: {
    background: '#BFBEBE'
  }
}

export function AgriculturalMachineForm() {
  const history = useHistory();
  const [showManualRegister, setShowManualRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeOption[]>([]);
  const [vehicleTypesUnavailable, setVehicleTypesUnavailable] = useState(false);

  const whitelabelCompany = whitelabelService.getWBDataCompany();
  const allowVehicleType = whitelabelCompany.allowAgriculturalMachineVehicleType;
  const [errors, setErrors] = useState({
    chassis: '',
    vehicle_type: '',
    brand: '',
    model: '',
    version: '',
    year_manufacture: '',
  });

  const [chassis, setChassis] = useState('');
  const [vehicleData, setVehicleData] = useState<any>(
    {
      model: '',
      brand: '',
      version: '',
      vehicle_type: '',
      year_manufacture: '',
      year_model: '',
      vehicle_type_code: '',
    }
  );

  const isBtnAble = !!chassis.length;
  const showVehicleTypeSelect = allowVehicleType && !vehicleTypesUnavailable;

  useEffect(() => {
    if (allowVehicleType && showManualRegister) {
      agriculturalMachineServices.getVehicleTypes()
        .then(setVehicleTypes)
        .catch((err) => {
          // 403 = company não habilitada para o catálogo; volta para o campo livre
          if (err.response?.status === HTTP_FORBIDDEN) {
            setVehicleTypesUnavailable(true);
            return;
          }

          setErrors((prevErrors) => ({ ...prevErrors, vehicle_type: VEHICLE_TYPES_ERROR_MESSAGE }));
        });
    }
  }, [allowVehicleType, showManualRegister]);

  const handleChangeChassis = (e: any) => {
    const { value } = e.target;
    setChassis(value);

    //validação  de chassi
    if (value.length > 17) {
      setErrors({ ...errors, chassis: 'Chassi não deve ter mais que 17 caracteres.' });
    } else {
      setErrors({ ...errors, chassis: '' });
    }
  };

  const onChangeVehicleData = (e: any) => {
    const { name, value } = e.target;

    setVehicleData((oldData: any) => ({
      ...oldData,
      [name]: value
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: ''
    }));
  }

  const onChangeYear = (e: any) => {
    // type="number" ignora maxLength, então o campo é texto e só guarda até 4 dígitos
    const { name, value } = e.target;
    onChangeVehicleData({ target: { name, value: value.replace(/\D/g, '').slice(0, YEAR_LENGTH) } });
  }

  const onChangeVehicleTypeCode = (e: any) => {
    const { value } = e.target;
    const selectedType = vehicleTypes.find((vehicleType) => vehicleType.code === value);

    // O store ainda exige vehicle_type, então enviamos o label junto com o code
    setVehicleData((oldData: any) => ({
      ...oldData,
      vehicle_type: selectedType?.label || '',
      vehicle_type_code: value
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      vehicle_type: ''
    }));
  }

  const getAuth = () => {
    authService.getToken()
      .then((result) => {
        const user: AuthUser = new AuthUser();
        user.accessToken = result.token;

        authService.saveAuth(user.formatAuthPayload());
      })
      .catch();
  }

  const goToPlateConfirmation = () => {
    history.push('/plate-confirmation');
  };

  const checkIdentifier = () => {
    agriculturalMachineServices.getInfo(chassis)
      .then((result: AxiosResponse) => {
        const { data } = result.data;
        const { inspection_value } = data;
        const plateInfo = new PlateInfo();
        plateInfo.chassis = chassis;
        plateInfo.branchModel = data.branch_model;

        storageService.setObject('plateInfo', plateInfo);
        localStorage.setItem(LocalStoreKeysEnum.LABEL_INSPECTION_PRICE, inspection_value);

        getAuth();
        goToPlateConfirmation();
      }).catch((err) => {
        setShowManualRegister(true);
        getAuth();
      }).finally(() => {
        setLoading(false);
      });
  }

  const validateVehicleType = () => {
    agriculturalMachineServices.validateVehicleType(chassis.trim())
      .then((validation: VehicleTypeValidation) => {
        if (validation.status === VehicleTypeValidationStatusEnum.NOT_ACCEPTED) {
          setErrors((prevErrors) => ({ ...prevErrors, chassis: validation.message || '' }));
          setLoading(false);
          return;
        }

        if (validation.status === VehicleTypeValidationStatusEnum.MANUAL_FILL_REQUIRED) {
          setShowManualRegister(true);
          getAuth();
          setLoading(false);
          return;
        }

        checkIdentifier();
      })
      .catch((err) => {
        // 403 = company fora da regra; segue o fluxo antigo sem a validação de tipo.
        // Qualquer outro erro trava o avanço: cair no fluxo antigo pularia a checagem de tipo
        if (err.response?.status === HTTP_FORBIDDEN) {
          checkIdentifier();
          return;
        }

        const chassisError = err.response?.status === HTTP_TOO_MANY_REQUESTS
          ? TOO_MANY_REQUESTS_MESSAGE
          : err.response?.data?.errors?.chassis?.[0] || err.response?.data?.message || '';

        setErrors((prevErrors) => ({ ...prevErrors, chassis: chassisError }));
        setLoading(false);
      });
  }

  const saveVehicleData = () => {
    if (!YEAR_FORMAT.test(vehicleData.year_manufacture)) {
      setErrors((prevErrors) => ({ ...prevErrors, year_manufacture: YEAR_FORMAT_ERROR_MESSAGE }));
      setLoading(false);
      return;
    }

    const agriculturalMachine = new AgricituralMachine();
    agriculturalMachine.chassis = chassis.trim();
    agriculturalMachine.vehicleData = new VehicleData(vehicleData);

    agriculturalMachineServices.saveVehicleData(agriculturalMachine)
      .then((result: AxiosResponse) => {
        const { data } = result;
        const { inspection_value, inspection_id } = data;
        const plateInfo = new PlateInfo();
        plateInfo.chassis = chassis;

        storageService.setObject('plateInfo', plateInfo);
        localStorage.setItem(LocalStoreKeysEnum.LABEL_INSPECTION_PRICE, inspection_value);
        localStorage.setItem(LocalStoreKeysEnum.LABEL_INSPECTION_ID, inspection_id);

        goToPlateConfirmation();
      }).catch((err) => {
        const responseErrors = err.response?.data?.errors || {};
        // A regra de tipo do backend responde 422 só com message, sem o mapa de errors
        const vehicleTypeError = responseErrors["vehicle_data.vehicle_type"]?.[0]
          || responseErrors["vehicle_data.vehicle_type_code"]?.[0]
          || (!err.response?.data?.errors && allowVehicleType ? err.response?.data?.message : '')
          || '';

        setErrors({
          chassis: responseErrors.chassis ? responseErrors.chassis[0] : '',
          brand: responseErrors["vehicle_data.brand"] ? responseErrors["vehicle_data.brand"][0] : '',
          model: responseErrors["vehicle_data.model"] ? responseErrors["vehicle_data.model"][0] : '',
          version: responseErrors["vehicle_data.version"] ? responseErrors["vehicle_data.version"][0] : '',
          year_manufacture: responseErrors["vehicle_data.year_manufacture"] ? responseErrors["vehicle_data.year_manufacture"][0] : '',
          vehicle_type: vehicleTypeError,
        });

        setShowManualRegister(true);
      }).finally(() => {
        setLoading(false);
      });
  }

  const formAction = () => {
    if (loading) {
      return;
    }

    setLoading(true);
    if (showManualRegister) {
      saveVehicleData();
      return;
    }

    if (allowVehicleType) {
      validateVehicleType();
      return;
    }

    checkIdentifier();
  }

  return (
    <>
      <>
        <InputIdentifier
          type="text"
          name="plate"
          data-testid="input-plate"
          onChange={handleChangeChassis}
          placeholder="Digite o chassi do veículo"
        />
        {errors.chassis && (
          <ErrorMessage
            style={{ margin: '-10px 5px' }}
          >
            {errors.chassis}
          </ErrorMessage>
        )}

        {showManualRegister && (
          <ManualForm>
            <ManualFormLabel>Tipo de veículo</ManualFormLabel>
            {showVehicleTypeSelect ? (
              <InputIdentifier
                as="select"
                name="vehicle_type_code"
                data-testid="select-vehicle-type"
                value={vehicleData.vehicle_type_code}
                onChange={onChangeVehicleTypeCode}
              >
                <option value="" disabled>Selecione o tipo de veículo</option>
                {vehicleTypes.map((vehicleType) => (
                  <option
                    key={vehicleType.code}
                    value={vehicleType.code}
                    data-testid="option-vehicle-type"
                  >{vehicleType.label}</option>
                ))}
              </InputIdentifier>
            ) : (
              <InputIdentifier
                type="text"
                name="vehicle_type"
                data-testid="input-plate"
                placeholder="Tipo de veículo"
                onChange={(e: any) => { onChangeVehicleData(e) }}
              />
            )}
            {errors.vehicle_type && (
              <ErrorMessage>
                {errors.vehicle_type}
              </ErrorMessage>
            )}

            <ManualFormLabel>Marca</ManualFormLabel>
            <InputIdentifier
              type="text"
              name="brand"
              data-testid="input-plate"
              placeholder="Marca"
              onChange={(e: any) => { onChangeVehicleData(e) }}
            />
            {errors.brand && (
              <ErrorMessage>
                {errors.brand}
              </ErrorMessage>
            )}

            <ManualFormLabel>Modelo</ManualFormLabel>
            <InputIdentifier
              type="text"
              name="model"
              data-testid="input-plate"
              placeholder="Modelo"
              onChange={(e: any) => { onChangeVehicleData(e) }}
            />
            {errors.model && (
              <ErrorMessage>
                {errors.model}
              </ErrorMessage>
            )}

            <ManualFormLabel>Versão</ManualFormLabel>
            <InputIdentifier
              type="text"
              name="version"
              data-testid="input-plate"
              placeholder="Versão"
              onChange={(e: any) => { onChangeVehicleData(e) }}
            />
            {errors.version && (
              <ErrorMessage>
                {errors.version}
              </ErrorMessage>
            )}

            <ManualFormLabel>Ano</ManualFormLabel>
            <InputIdentifier
              name="year_manufacture"
              data-testid="input-plate"
              placeholder="Ano"
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={vehicleData.year_manufacture}
              onChange={onChangeYear}
            />
            {errors.year_manufacture && (
              <ErrorMessage>
                {errors.year_manufacture}
              </ErrorMessage>
            )}
          </ManualForm>
        )}
        <ConfirmButton
          // disabled={!isBtnAble}
          type="button"
          style={!isBtnAble ? identifierStyle.disabledBtn : {}}
          onClick={formAction}
        >
          {!loading && ('Continuar compra')}
          {loading && (
            <CircularProgress sx={{ color: '#fff' }} size={23} />
          )}
        </ConfirmButton>
      </>
    </>
  );
}
